/**
 * Liability Panel — slide-out detail panel for a single liability entry,
 * with inline editing following the same pattern as SecurityInterestDetailPanel.
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useConvexMutation, convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import ReactMarkdown from "react-markdown";
import {
  X,
  FileText,
  ChevronRight,
  Pencil,
  Check,
  Trash2,
  ArrowLeftRight,
  GitMerge,
  AlertTriangle,
  EyeOff,
  Eye,
  // Lucide User marks the contact-card action on a linked creditor name.
  User,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { RadioGroup, RadioGroupItem } from "@/ui/primitives/radio-group";
import { Textarea } from "@/ui/primitives/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { cn } from "@/utils/misc";
import type { Id } from "@cvx/_generated/dataModel";
import {
  ChangeHistory,
  type ResolvedHistoryUser,
} from "../shared-panels/ChangeHistory";
import {
  RANKING_LABELS,
  creditorRanking,
  rankingToFlags,
  type CreditorRanking,
} from "./ranking";
import { RelatedPartyBadge } from "@/components/jobs/phases/_shared/RelatedPartyBadge";
import { relatedPartyExplanation } from "@/components/jobs/phases/_shared/relatedPartyShared";
import { SlideOverPanel } from "../shared-panels/SlideOverPanel";
import { LinkedRecordsSection } from "../shared-panels/LinkedRecordsSection";
import { SuggestedLinksSection } from "../shared-panels/SuggestedLinksSection";
import {
  ConfirmEditDialog,
  type PendingEdit,
} from "../shared-panels/ConfirmEditDialog";
import type { RecordType } from "../shared-panels/linkShared";
import { DocumentDetail } from "../../documents/DocumentDetail";
import type { LiabilityEntryUI } from "./types";
import {
  LIABILITY_FIELD_FORMATTERS,
  LIABILITY_FIELD_LABELS,
} from "./historyFormatters";
import { AddSecurityInterestPanel } from "../securityInterests/AddSecurityInterestPanel";
import { OwedAmountBanner, OwedEstimatesSection } from "./OwedEstimatesSection";
import type { PodStatusRow } from "@/components/jobs/phases/recoveries/podShared";
import { PodStatusSection } from "@/components/jobs/phases/recoveries/podStatusDisplay";

// ---------------------------------------------------------------------------
// Labels / options
// ---------------------------------------------------------------------------

const NATURE_OPTIONS: Record<string, string> = {
  trade: "Trade",
  lease: "Lease",
  tax: "Tax",
  utility: "Utility",
  professional_fees: "Professional Fees",
  loan: "Loan",
  insurer: "Insurer",
  other: "Other",
};

const SOURCE_OPTIONS: Record<string, string> = {
  aged_payables: "Aged Payables",
  invoice: "Invoice",
  contract: "Contract",
  ledger: "Ledger",
  bank_statement: "Bank Statement",
  director_disclosure: "Director Disclosure",
  proof_of_debt: "Proof of Debt",
  other: "Other",
};

const CONFIDENCE_OPTIONS: Record<string, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function normaliseNotesForMarkdown(raw: string): string {
  return raw.replace(/\s*•\s*/g, "\n- ").trim();
}

const fmtAud = (v: number): string =>
  `$${v.toLocaleString("en-AU", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="pb-1.5 pt-6 text-xs font-medium uppercase tracking-widest text-[#121212]">
      {children}
    </div>
  );
}

function SelectField({
  label,
  value,
  options,
  field,
  onSave,
  isViewer,
}: {
  label: string;
  value: string;
  options: Record<string, string>;
  field: string;
  onSave: (f: string, v: string) => void;
  isViewer?: boolean;
}) {
  if (isViewer) {
    return (
      <div className="flex items-center justify-between gap-4 py-2">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className="text-sm text-foreground">
          {options[value] ?? value}
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      <Select value={value} onValueChange={(v) => onSave(field, v)}>
        <SelectTrigger className="h-7 w-44 text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(options).map(([k, v]) => (
            <SelectItem key={k} value={k}>
              {v}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Collapsible markdown notes (mirrors LoanDetailPanel / AssetPanel)
// ---------------------------------------------------------------------------

function NotesSection({
  notes,
  onSave,
  isViewer,
}: {
  notes: string;
  onSave: (value: string) => void;
  isViewer?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState(notes ?? "");

  const handleSave = () => {
    if (draft !== (notes ?? "")) onSave(draft);
    setEditing(false);
  };

  const normalisedNotes = notes ? normaliseNotesForMarkdown(notes) : "";

  return (
    <>
      <div className="flex items-center justify-between pb-1.5 pt-6">
        <div className="text-xs font-medium uppercase tracking-widest text-foreground">
          Notes
        </div>
        {!isViewer && !editing && (
          <button
            onClick={() => {
              setDraft(notes ?? "");
              setEditing(true);
            }}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <Pencil className="h-3 w-3" />
          </button>
        )}
      </div>
      {editing ? (
        <div>
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Add notes (supports **bold** and - bullet points)..."
            className="mt-1 min-h-[160px] resize-y p-3 text-sm leading-relaxed"
            autoFocus
          />
          <div className="mt-2 flex gap-2">
            <button
              onClick={handleSave}
              className="rounded bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Save
            </button>
            <button
              onClick={() => setEditing(false)}
              className="rounded px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div
            className={cn(
              "prose prose-sm relative max-w-none overflow-hidden py-2 transition-[max-height] duration-200 dark:prose-invert [&_li]:text-sm [&_li]:leading-relaxed [&_p]:text-sm [&_p]:leading-relaxed [&_p]:text-foreground [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-4",
              !expanded && "max-h-[180px]",
            )}
          >
            {normalisedNotes ? (
              <ReactMarkdown>{normalisedNotes}</ReactMarkdown>
            ) : (
              <p className="text-muted-foreground">&mdash;</p>
            )}
            {normalisedNotes && !expanded && (
              <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background to-transparent" />
            )}
          </div>
          {normalisedNotes && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="mt-2 rounded-md border border-primary/30 px-3 py-1 text-xs font-medium text-primary transition-colors hover:border-primary/50 hover:bg-primary/5"
            >
              {expanded ? "Show less" : "Show more"}
            </button>
          )}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Merge
// ---------------------------------------------------------------------------

/**
 * Inline merge control — the manual counterpart of the agent's automatic
 * duplicate grouping, for creditors it deliberately leaves split (e.g. one
 * record per contract under the same financier). Rendered in the panel body
 * rather than a dialog: this panel is already a slide-over, and a nested
 * overlay renders behind it.
 */
function MergeSection({
  entry,
  onCancel,
  onMerged,
}: {
  entry: LiabilityEntryUI;
  onCancel: () => void;
  onMerged: (targetId: string) => void;
}) {
  const [targetId, setTargetId] = useState<string>("");
  const [amountStrategy, setAmountStrategy] = useState<"combine" | "highest">(
    "combine",
  );

  // Same subscription as the creditors table behind the panel, so this costs
  // nothing extra while the panel is open.
  const { data: entries } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.listLiabilityEntriesForJob,
      {
        jobId: entry.jobId,
      },
    ),
  );

  const mergeEntries = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.liabilities.publicMutations
        .mergeLiabilityEntries,
    ),
  });

  const targets = useMemo(() => {
    const rows = (entries ?? []) as LiabilityEntryUI[];
    return rows
      .filter((e) => e._id !== entry._id && e.status === "active")
      .sort((a, b) => a.creditorName.localeCompare(b.creditorName));
  }, [entries, entry._id]);

  const target = targets.find((t) => t._id === targetId);

  const handleMerge = async () => {
    if (!target) return;
    try {
      await mergeEntries.mutateAsync({
        sourceId: entry._id as Id<"liabilityEntries">,
        targetId: target._id as Id<"liabilityEntries">,
        amountStrategy,
      });
      toast.success("Creditors merged");
      onMerged(target._id);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to merge creditors",
      );
    }
  };

  return (
    <div className="mx-5 mt-4 rounded-lg border bg-muted/40 p-3">
      <p className="text-xs font-semibold">Merge into another creditor</p>
      <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">
        Use this when one creditor is on the register twice — the same debt
        recorded under two names, or separate contracts that belong together.
        The proofs of debt, linked records, figures and source documents on{" "}
        <strong>{entry.creditorName}</strong> move to the creditor you pick, and
        this record is removed.
      </p>

      <div className="mt-2.5">
        <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Merge into
        </label>
        <Select value={targetId} onValueChange={setTargetId}>
          <SelectTrigger className="mt-1 text-sm">
            <SelectValue placeholder="Select a creditor…" />
          </SelectTrigger>
          <SelectContent>
            {targets.map((t) => (
              <SelectItem key={t._id} value={t._id}>
                {t.creditorName}
                {t.amount > 0 ? ` — ${fmtAud(t.amount)}` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-2.5">
        <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Amounts owed
        </label>
        <RadioGroup
          value={amountStrategy}
          onValueChange={(v) => setAmountStrategy(v as "combine" | "highest")}
          className="mt-1.5 gap-1.5"
        >
          <label className="flex cursor-pointer items-start gap-2">
            <RadioGroupItem value="combine" className="mt-0.5 shrink-0" />
            <span className="text-xs leading-relaxed">
              <span className="font-medium">Add them together</span>
              <span className="text-muted-foreground">
                {" "}
                — separate debts to the same creditor, e.g. one per contract
              </span>
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-2">
            <RadioGroupItem value="highest" className="mt-0.5 shrink-0" />
            <span className="text-xs leading-relaxed">
              <span className="font-medium">Keep the higher figure</span>
              <span className="text-muted-foreground">
                {" "}
                — the same debt recorded twice
              </span>
            </span>
          </label>
        </RadioGroup>
      </div>

      {target && target.natureOfDebt !== entry.natureOfDebt && (
        <p className="mt-2 flex items-start gap-1.5 text-[11px] leading-relaxed text-amber-700 dark:text-amber-400">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
          <span>
            These records hold different types of debt (
            {NATURE_OPTIONS[entry.natureOfDebt] ?? entry.natureOfDebt} vs{" "}
            {NATURE_OPTIONS[target.natureOfDebt] ?? target.natureOfDebt}). Debts
            of different types are usually kept as separate creditors.
          </span>
        </p>
      )}

      {target && (
        <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
          &ldquo;{entry.creditorName}&rdquo; will be folded into &ldquo;
          {target.creditorName}&rdquo;. The removed record is kept in the
          history.
        </p>
      )}

      <div className="mt-3 flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={!target || mergeEntries.isPending}
          onClick={handleMerge}
        >
          {mergeEntries.isPending ? "Merging…" : "Merge"}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface LiabilityPanelProps {
  entry: LiabilityEntryUI | null;
  open: boolean;
  onClose: () => void;
  onDeleted?: () => void;
  /**
   * Called after this creditor is merged into another, with the survivor's id.
   * When absent, the panel falls back to the delete behaviour (close).
   */
  onMerged?: (targetId: string) => void;
  isViewer?: boolean;
  placement?: "primary" | "secondary";
  /** Position in the secondary panel stack (1 = closest to primary). */
  stackIndex?: number;
  onOpenLinked?: (type: RecordType, id: string) => void;
  /** Opens the linked job CRM contact card when the creditor has one. */
  onViewContact?: () => void;
}

/**
 * Proof of Debt status on the creditor slide-out. Looks up this row in the
 * job-wide status map the creditors table already subscribes to.
 */
function LiabilityPodSection({
  jobId,
  entryId,
}: {
  jobId: Id<"jobs">;
  entryId: string;
}) {
  const { data: rawPodStatuses } = useQuery(
    convexQuery(
      api.jobs.recoveries.publicQueries.listPodStatusForLiabilityEntries,
      { jobId },
    ),
  );
  const status = useMemo(() => {
    for (const row of (rawPodStatuses ?? []) as PodStatusRow[]) {
      if (row.liabilityEntryId === entryId) return row;
    }
    return undefined;
  }, [rawPodStatuses, entryId]);

  return <PodStatusSection jobId={jobId} status={status} />;
}

/**
 * Says so when this creditor is also on the related parties register. Derived
 * there by name, so it appears the moment the register identifies them —
 * nothing is stored on the creditor row.
 *
 * Its own component so the lookup only runs for a panel that has an entry.
 */
function RelatedPartyNote({
  jobId,
  entryId,
}: {
  jobId: Id<"jobs">;
  entryId: string;
}) {
  const { data: relatedFlags } = useQuery(
    convexQuery(
      api.jobs.summarization.agents.relatedParties.publicQueries
        .listRelatedPartyFlagsForJob,
      { jobId },
    ),
  );

  const match = relatedFlags?.creditors.find((c) => c.entryId === entryId);
  if (!match) return null;

  return (
    <div className="mt-1.5 flex items-center gap-1.5">
      <RelatedPartyBadge match={match} />
      <span className="text-xs text-muted-foreground">
        {relatedPartyExplanation(match)}
      </span>
    </div>
  );
}

export function LiabilityPanel({
  entry,
  open,
  onClose,
  onDeleted,
  onMerged,
  isViewer,
  placement = "primary",
  stackIndex,
  onOpenLinked,
  onViewContact,
}: LiabilityPanelProps) {
  const [viewingDocumentId, setViewingDocumentId] =
    useState<Id<"documents"> | null>(null);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [docsExpanded, setDocsExpanded] = useState(true);
  // Edit awaiting practitioner confirmation (all inline edits pass through
  // the shared ConfirmEditDialog before any mutation fires).
  const [pendingEdit, setPendingEdit] = useState<PendingEdit | null>(null);

  const updateField = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.liabilities.publicMutations
        .updateLiabilityField,
    ),
  });
  const deleteEntry = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.liabilities.publicMutations.deleteLiability,
    ),
  });
  const toggleExcluded = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.liabilities.publicMutations.toggleExcluded,
    ),
  });

  // Resolve practitioner identities (full name + avatar) for any
  // history entry that carries a `userId` foreign key. One Convex
  // subscription per panel, deduped on the backend, so a 15-entry
  // history with one editor only fetches one user.
  const historyUserIds = useMemo<Array<Id<"users">>>(() => {
    if (!entry?.changeHistory) return [];
    const ids = new Set<Id<"users">>();
    for (const change of entry.changeHistory) {
      if (change.userId) ids.add(change.userId);
    }
    return Array.from(ids);
  }, [entry?.changeHistory]);

  const { data: historyUsers } = useQuery({
    ...convexQuery(api.users.queries.listUsersByIds, {
      ids: historyUserIds,
    }),
    enabled: historyUserIds.length > 0,
  });

  const historyUsersById = useMemo(() => {
    const map = new Map<Id<"users">, ResolvedHistoryUser>();
    for (const u of historyUsers ?? []) {
      map.set(u._id, u);
    }
    return map;
  }, [historyUsers]);

  useEffect(() => {
    if (open) {
      setConfirmDelete(false);
      setMergeOpen(false);
      setConvertOpen(false);
    }
  }, [open]);

  if (!entry) return null;

  // Stages the edit behind the shared confirmation dialog — the mutation only
  // fires once the practitioner confirms (optionally leaving a note).
  const handleFieldSave = (field: string, value: unknown) => {
    // "Ranks as" is one control over two stored flags, so both are written —
    // otherwise moving a row from secured to preferred would leave it secured.
    if (field === "ranking") {
      setPendingEdit({
        fieldLabel: "Ranks as",
        onConfirm: (note) => {
          const flags = rankingToFlags(value as CreditorRanking);
          const writes = Object.entries(flags)
            .filter(
              ([name, flag]) =>
                flag !== (entry[name as keyof typeof flags] ?? false),
            )
            .map(([name, flag]) =>
              updateField.mutateAsync({
                entryId: entry._id as Id<"liabilityEntries">,
                field: name,
                value: flag,
                note,
              }),
            );
          return Promise.all(writes);
        },
      });
      return;
    }
    setPendingEdit({
      fieldLabel: LIABILITY_FIELD_LABELS[field] ?? field,
      onConfirm: (note) =>
        updateField.mutateAsync({
          entryId: entry._id as Id<"liabilityEntries">,
          field,
          value,
          note,
        }),
    });
  };

  const handleDelete = () => {
    deleteEntry.mutate(
      { entryId: entry._id as Id<"liabilityEntries"> },
      {
        onSuccess: () => {
          onDeleted?.();
          onClose();
        },
      },
    );
  };

  return (
    <>
      <SlideOverPanel
        open={open}
        onClose={onClose}
        placement={placement}
        stackIndex={stackIndex}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 border-b bg-background/95 px-5 pb-3 pt-5 backdrop-blur-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              {/* Editable creditor name */}
              {!isViewer && editingName ? (
                <div className="flex items-center gap-1.5">
                  <Input
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    className="h-8 text-lg font-semibold"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleFieldSave("creditorName", nameDraft);
                        setEditingName(false);
                      }
                      if (e.key === "Escape") setEditingName(false);
                    }}
                    autoFocus
                  />
                  <button
                    onClick={() => {
                      handleFieldSave("creditorName", nameDraft);
                      setEditingName(false);
                    }}
                    className="p-1 text-emerald-600"
                  >
                    <Check className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setEditingName(false)}
                    className="p-1 text-muted-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div
                  className={cn("group", !isViewer && "cursor-pointer")}
                  onClick={() => {
                    if (!isViewer) {
                      setNameDraft(entry.creditorName);
                      setEditingName(true);
                    }
                  }}
                >
                  <div className="flex items-center gap-1.5">
                    <h2 className="text-lg font-semibold leading-snug text-foreground">
                      {entry.creditorName}
                    </h2>
                    {!isViewer && (
                      <Pencil className="mt-1 h-3.5 w-3.5 text-muted-foreground/0 transition-colors group-hover:text-muted-foreground" />
                    )}
                  </div>
                </div>
              )}
              <RelatedPartyNote
                jobId={entry.jobId as Id<"jobs">}
                entryId={entry._id}
              />
            </div>

            <div className="mt-0.5 flex shrink-0 items-center gap-1">
              {onViewContact && (
                <button
                  type="button"
                  onClick={onViewContact}
                  className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  title="Open contact card"
                >
                  <User className="h-[16px] w-[16px]" />
                </button>
              )}
              {!isViewer && (
                <button
                  onClick={() => {
                    setMergeOpen((prev) => !prev);
                    setConfirmDelete(false);
                  }}
                  className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  title="Merge into another creditor"
                >
                  <GitMerge className="h-[16px] w-[16px]" />
                </button>
              )}
              {!isViewer && (
                <button
                  onClick={() => setConvertOpen(true)}
                  className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  title="Convert to security interest"
                >
                  <ArrowLeftRight className="h-[16px] w-[16px]" />
                </button>
              )}
              {!isViewer && (
                <button
                  onClick={() =>
                    toggleExcluded.mutate({
                      entryId: entry._id as Id<"liabilityEntries">,
                      isExcluded: !entry.isExcluded,
                    })
                  }
                  className={cn(
                    "rounded-lg p-1.5 transition-colors hover:bg-muted",
                    entry.isExcluded
                      ? "text-emerald-600 hover:text-emerald-700 dark:text-emerald-400"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                  title={
                    entry.isExcluded
                      ? "Include in totals"
                      : "Exclude from totals"
                  }
                >
                  {entry.isExcluded ? (
                    <Eye className="h-[16px] w-[16px]" />
                  ) : (
                    <EyeOff className="h-[16px] w-[16px]" />
                  )}
                </button>
              )}
              {!isViewer && (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30"
                  title="Delete"
                >
                  <Trash2 className="h-[16px] w-[16px]" />
                </button>
              )}
              <button
                onClick={onClose}
                className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <X className="h-[18px] w-[18px]" />
              </button>
            </div>
          </div>
        </div>

        {/* Excluded notice */}
        {entry.isExcluded && (
          <div className="mx-5 mt-4 flex items-start gap-2 rounded-lg border border-border bg-muted/50 px-3 py-2">
            <EyeOff className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <p className="text-xs leading-relaxed text-muted-foreground">
              This creditor is excluded — it stays in the register but is left
              out of the liability totals, the Proof of Debt process and
              reporting.
            </p>
          </div>
        )}

        <OwedAmountBanner entry={entry} isViewer={isViewer} />

        {/* Delete confirmation */}
        {confirmDelete && (
          <div className="mx-5 mt-4 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/30">
            <p className="text-sm text-red-700 dark:text-red-300">
              Remove this creditor?
            </p>
            <div className="mt-2 flex gap-2">
              <Button
                size="sm"
                variant="destructive"
                onClick={handleDelete}
                disabled={deleteEntry.isPending}
              >
                {deleteEntry.isPending ? "Removing..." : "Remove"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setConfirmDelete(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Merge into another creditor */}
        {mergeOpen && !isViewer && (
          <MergeSection
            key={entry._id}
            entry={entry}
            onCancel={() => setMergeOpen(false)}
            onMerged={(targetId) => {
              setMergeOpen(false);
              if (onMerged) {
                onMerged(targetId);
              } else {
                onDeleted?.();
                onClose();
              }
            }}
          />
        )}

        <div className="px-5 pb-8">
          {/* The figures behind the headline in the banner above */}
          <OwedEstimatesSection entry={entry} isViewer={isViewer} />
          <div className="mt-4 border-b" />

          {/* Classification */}
          <SectionHeader>Classification</SectionHeader>
          <SelectField
            label="Type of debt"
            value={entry.natureOfDebt}
            options={NATURE_OPTIONS}
            field="natureOfDebt"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <SelectField
            label="Source"
            value={entry.source}
            options={SOURCE_OPTIONS}
            field="source"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <SelectField
            label="Confidence"
            value={entry.confidence}
            options={CONFIDENCE_OPTIONS}
            field="confidence"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <div className="mt-2 border-b" />

          {/* Ranking */}
          <SectionHeader>Ranking</SectionHeader>
          <SelectField
            label="Ranks as"
            value={creditorRanking(entry)}
            options={RANKING_LABELS}
            field="ranking"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          {creditorRanking(entry) === "preferential" && (
            <p className="pb-2 text-xs leading-relaxed text-muted-foreground">
              Ranks ahead of ordinary unsecured creditors. Employee entitlements
              are captured in the Employees register — recording them here as
              well would count them twice.
            </p>
          )}
          {entry.secured &&
            (isViewer ? (
              <p className="py-2 text-sm leading-relaxed text-foreground/80">
                {entry.securityDetails || "\u2014"}
              </p>
            ) : (
              <Textarea
                key={entry._id + "-securityDetails"}
                defaultValue={entry.securityDetails ?? ""}
                onBlur={(e) => {
                  if (e.target.value !== (entry.securityDetails ?? ""))
                    handleFieldSave(
                      "securityDetails",
                      e.target.value || undefined,
                    );
                }}
                placeholder="Add security details..."
                className="mt-2 min-h-[100px] resize-y p-3 text-sm leading-relaxed"
              />
            ))}
          <div className="mt-2 border-b" />

          {/* Proof of Debt — same received / adjudicated status as the table,
              with a link through to the claim in Recoveries. */}
          <LiabilityPodSection
            jobId={entry.jobId as Id<"jobs">}
            entryId={entry._id}
          />
          <div className="mt-2 border-b" />

          {/* Practitioner notes */}
          <NotesSection
            notes={entry.notes ?? ""}
            onSave={(value) => handleFieldSave("notes", value)}
            isViewer={isViewer}
          />
          <div className="mt-4 border-b" />

          {/* Source documents */}
          {entry.sourceDocuments && entry.sourceDocuments.length > 0 && (
            <>
              <button
                type="button"
                onClick={() => setDocsExpanded((prev) => !prev)}
                className="group flex w-full items-center justify-between gap-2 pb-1.5 pt-6 text-xs font-medium uppercase tracking-widest text-[#121212]"
              >
                <span>Source documents ({entry.sourceDocuments.length})</span>
                <ChevronRight
                  className={cn(
                    "h-4 w-4 text-muted-foreground transition-transform",
                    docsExpanded && "rotate-90",
                  )}
                />
              </button>
              {docsExpanded && (
                <div className="flex flex-col gap-2 pt-2.5">
                  {entry.sourceDocuments.map((doc, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() =>
                        setViewingDocumentId(doc.documentId as Id<"documents">)
                      }
                      className="group flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors hover:bg-muted/50"
                    >
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
                        <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-foreground">
                          {doc.fileName}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/30 transition-colors group-hover:text-muted-foreground" />
                    </button>
                  ))}
                </div>
              )}
              <div className="mt-4 border-b" />
            </>
          )}

          {/* Linked records */}
          {onOpenLinked && (
            <div className="mt-4 space-y-4">
              <SuggestedLinksSection
                jobId={entry.jobId}
                anchor={{ type: "creditor", id: entry._id }}
                onOpenLinked={onOpenLinked}
                isViewer={isViewer}
              />
              <LinkedRecordsSection
                jobId={entry.jobId}
                anchor={{ type: "creditor", id: entry._id }}
                onOpenLinked={onOpenLinked}
                isViewer={isViewer}
              />
            </div>
          )}

          {/* History */}
          {entry.changeHistory && (
            <div className="mt-4">
              <ChangeHistory
                history={entry.changeHistory}
                usersById={historyUsersById}
                formatters={LIABILITY_FIELD_FORMATTERS}
                fieldLabels={LIABILITY_FIELD_LABELS}
                persistId="liability-detail:history"
              />
            </div>
          )}
        </div>
      </SlideOverPanel>

      <DocumentDetail
        documentId={viewingDocumentId}
        isViewer
        open={viewingDocumentId !== null}
        onClose={() => setViewingDocumentId(null)}
      />

      {/* Convert -> Security Interest sheet */}
      {!isViewer && (
        <AddSecurityInterestPanel
          jobId={entry.jobId}
          taskId={entry.taskId}
          open={convertOpen}
          onClose={() => setConvertOpen(false)}
          onCreated={() => {
            setConvertOpen(false);
            onDeleted?.();
            onClose();
          }}
          convertFromLiabilityId={entry._id as Id<"liabilityEntries">}
          prefill={{
            securedPartyName: entry.creditorName,
            collateralDescription:
              entry.securityDetails || entry.notes || entry.creditorName,
            owedAmount: entry.amount,
            owedAmountAsAt: entry.date,
            notes: entry.notes,
          }}
        />
      )}

      {/* Confirmation gate for every inline edit in this panel */}
      <ConfirmEditDialog
        edit={pendingEdit}
        onClose={() => setPendingEdit(null)}
      />
    </>
  );
}
